import {
  Button,
  Stack,
  Image,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react"
import defaultGameData from "../default"
import type { BaseSelection } from "@types"
import { GameText } from "../../components/GameText"
import { motion } from "framer-motion"
import { lookupGameIcon } from "../../components/GameIcon"
import {
  checkGameRules,
  getRandomValidTriggerOrSlide,
  processTurn,
  checkForZeroMeters,
} from "../../app/gameLogic"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  incrementGameTurn,
  resetGame,
  selectSlide,
  updateGameSlide,
  setGame,
  setMentorModalShown,
  selectMentorModalShown,
} from "./gameSlice"
import { useEffect, useState } from "react"
import { GameSummary } from "./GameSummary"

const SlideViewer: React.FC = () => {
  const [slideRandomIndex, setSlideRandomIndex] = useState(0)
  const currentSlide = useAppSelector(selectSlide)
  const data = currentSlide ?? defaultGameData.content[0].groups[0].slides[0]

  const slide = currentSlide ?? data

  useEffect(() => {
    setSlideRandomIndex(Math.floor(Math.random() * 6))
  }, [slide])

  if (!slide) {
    return null
  }

  // Check if game is over (no more selections available)
  const isGameOver = slide.selections.length === 0

  // Show summary screen if game is over
  if (isGameOver) {
    return <GameSummary />
  }

  return (
    <Box>
      {/* Image with 1:1 aspect ratio, centered and cropped to fit */}
      <Box
        position="relative"
        width="100%"
        paddingTop="100%" // This creates a square aspect ratio
        borderRadius="2xl"
        overflow="hidden"
        bg="linear-gradient(135deg, #fef3c7 0%, #fbbf24 50%, #f59e0b 100%)"
      >
        <Image
          src={slide.image}
          alt={slide.imageAlt}
          objectFit="cover"
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
        />
        {/* Overlay description at bottom */}
        <Box
          position="absolute"
          bottom={0}
          left={0}
          width="100%"
          bg="rgba(0, 0, 0, 0.75)"
          color="white"
          p={4}
        >
          <GameText text={data.description} />
        </Box>
      </Box>
      {/* SlideButtonGroup below the image, full width */}
      <Box width="100%" mt={6}>
        <SlideButtonGroup
          index={slideRandomIndex}
          selections={slide.selections}
        />
      </Box>
    </Box>
  )
}

const SlideButtonGroup = (props: {
  index: number
  selections: BaseSelection[]
}) => {
  // Always reserve space for 4 buttons to prevent layout shifts
  const maxButtons = 4
  const paddedSelections = [...props.selections]
  while (paddedSelections.length < maxButtons) {
    paddedSelections.push(null as any)
  }

  return (
    <Stack
      spacing={4}
      mt={6}
      width="100%"
      direction={{ base: "column", md: "row" }}
      wrap="wrap"
      justifyContent="center"
      minHeight={`${maxButtons * 56 + (maxButtons - 1) * 16}px`} // Reserve space for 4 buttons (56px height + 16px spacing)
    >
      {paddedSelections
        .slice(0, maxButtons)
        .map((selection, index) =>
          selection ? (
            <SlideButton
              key={index}
              index={index + props.index}
              action={selection}
            />
          ) : (
            <Box key={index} height="56px" width="100%" visibility="hidden" />
          ),
        )}
    </Stack>
  )
}

export const ResetButton = () => {
  const dispatch = useAppDispatch()

  const onReset = () => {
    dispatch(resetGame())
  }

  return (
    <Button onClick={onReset} colorScheme="red" mt="4" w="100%">
      Start Over
    </Button>
  )
}

const SlideButton = (props: { index: number; action: BaseSelection }) => {
  // Dispatch
  const dispatch = useAppDispatch()
  const gameData = useAppSelector(state => state.game)
  const mentorModalShown = useAppSelector(selectMentorModalShown)

  const icon = lookupGameIcon(props.action?.icon)
  const color = "blue" // Single color for all buttons to avoid influencing choices

  // State for mentor code modal
  const [showMentorModal, setShowMentorModal] = useState(false)
  const [mentorCodeInput, setMentorCodeInput] = useState("")
  const [codeError, setCodeError] = useState(false)
  const [zeroMeters, setZeroMeters] = useState<string[]>([])
  const [pendingGameData, setPendingGameData] = useState<
    typeof gameData | null
  >(null)

  const handleMentorCodeSubmit = () => {
    // Code is correct, continue the game
    setShowMentorModal(false)
    setMentorCodeInput("")
    setCodeError(false)

    if (pendingGameData) {
      continueGameAfterMentorApproval(pendingGameData)
    }
  }

  const continueGameAfterMentorApproval = (
    updatedGameData: typeof gameData,
  ) => {
    // Increment the game turn
    dispatch(incrementGameTurn())

    // Update the entire game state with the processed data
    dispatch(
      setGame({
        age: updatedGameData.age,
        agency: updatedGameData.agency,
        education: updatedGameData.education,
        health: updatedGameData.health,
        relationship: updatedGameData.relationship,
        mentalHealth: updatedGameData.mentalHealth,
        physicalHealth: updatedGameData.physicalHealth,
        tags: updatedGameData.tags,
        history: updatedGameData.history,
      }),
    )

    // Check if game is over
    const isGameOver = !checkGameRules(updatedGameData, defaultGameData.rules)
    if (isGameOver) {
      console.log("Game over")
      dispatch(
        updateGameSlide({
          title: { en: `What an adventure!` },
          description: {
            en: "Try again to see where you end up next time.",
          },
          image: "/stop_slide.webp",
          imageAlt: "slide alt text",
          type: "event",
          selections: [],
        }),
      )
      return
    }

    let newSlide = null
    let tries = 0

    while (!newSlide) {
      newSlide = getRandomValidTriggerOrSlide(
        defaultGameData.content,
        updatedGameData,
      )
      if (newSlide) {
        dispatch(updateGameSlide(newSlide))
      } else {
        console.log("No new slide found")
      }
    }

    if (tries > 10) {
      console.log("No new slide found")
      return
    }
  }

  const onClick = () => {
    // Process the turn and get the updated game state
    const updatedGameData = processTurn(gameData, props.action)
    console.log("Updated stats", updatedGameData)

    // Check if any meters have reached zero
    const metersAtZero = checkForZeroMeters(updatedGameData)

    if (metersAtZero.length > 0 && !mentorModalShown) {
      // Pause the game and show mentor code modal (only if not shown before)
      console.log("Meters at zero:", metersAtZero)
      setZeroMeters(metersAtZero)
      setPendingGameData(updatedGameData)
      setShowMentorModal(true)
      dispatch(setMentorModalShown())
      return
    }

    // No meters at zero or modal already shown, continue normally
    continueGameAfterMentorApproval(updatedGameData)
  }

  return (
    <Box w="100%">
      <motion.a
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.9 }}
        onClick={onClick}
      >
        {icon ? (
          <Button
            size="lg"
            aria-label="choice"
            leftIcon={icon}
            w="100%"
            colorScheme={color}
          >
            <GameText text={props.action.title} />
          </Button>
        ) : (
          <Button
            size="lg"
            aria-label="choice"
            w="100%"
            colorScheme={color}
            style={{ whiteSpace: "initial" }}
          >
            <GameText text={props.action.title} />
          </Button>
        )}
      </motion.a>

      {/* Mentor Code Modal */}
      <Modal
        isOpen={showMentorModal}
        onClose={() => {}}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
        <ModalContent>
          <ModalHeader>Mentor Intervention Required</ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="warning" variant="left-accent">
                <AlertIcon />
                <Box>
                  <AlertTitle>Critical Level Reached</AlertTitle>
                  <AlertDescription>
                    The following meter(s) have reached zero:
                    <Text fontWeight="bold" mt={2}>
                      {zeroMeters.join(", ")}
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>

              <Text>
                A mentor must enter the code to continue the game and discuss
                this situation with the student.
              </Text>

              <Input
                placeholder="Enter mentor code"
                value={mentorCodeInput}
                onChange={e => {
                  setMentorCodeInput(e.target.value)
                  setCodeError(false)
                }}
                onKeyPress={e => {
                  if (e.key === "Enter") {
                    handleMentorCodeSubmit()
                  }
                }}
                isInvalid={codeError}
                errorBorderColor="red.500"
                size="lg"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3} w="100%">
              <Button
                colorScheme="blue"
                onClick={handleMentorCodeSubmit}
                flex={2}
              >
                Continue Game
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowMentorModal(false)
                  setMentorCodeInput("")
                  setCodeError(false)
                  if (pendingGameData) {
                    continueGameAfterMentorApproval(pendingGameData)
                  }
                }}
                flex={1}
              >
                Skip
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default SlideViewer
